/**
 * src/types/annotation.ts
 * =============================================================================
 * Tipos de dominio para anotaciones encima del PDF.
 *
 * Reglas clave:
 *  - Las coordenadas son SIEMPRE normalizadas 0..1 sobre las dimensiones
 *    NATURALES de la página del PDF. Así zoom/pan/cambio de DPI no rompen
 *    la posición — multiplicamos al renderizar.
 *  - Cada anotación va atada a una `page` 1-based. Al cambiar de página solo
 *    se renderizan las que matchean.
 *  - El `id` es un UUID v4 generado en cliente — sirve también de key React.
 *  - `createdAt` ISO 8601, útil para ordenar y para sync futura.
 *  - El campo discriminante es `type`. TS estrechará el tipo en cada caso.
 *
 * Persistencia inicial: localStorage por contexto (ver `annotationStorage.ts`).
 * Persistencia: tabla `anotaciones` en D1 (modelo descrito
 * en [[project_plans_data_model]]).
 * =============================================================================
 */

export type AnnotationType = 'point' | 'text' | 'draw' | 'line' | 'rect' | 'ellipse' | 'image'

interface BaseAnnotation {
  /**
   * Si esta anotación la escribió la persona que está viendo el documento.
   *
   * Lo calcula el servidor comparando la clave de autor que guarda el navegador
   * En enlaces se calcula con la clave anónima del navegador; con cuenta, con
   * `autor_user_id`. Puede faltar en datos antiguos, así que el visor solo
   * bloquea ante un `false` explícito.
   */
  mine?: boolean
  id: string
  type: AnnotationType
  /** Página 1-based del PDF en la que vive la anotación. */
  page: number
  /** Color en hex (#RRGGBB). */
  color: string
  /** ISO 8601. */
  createdAt: string
  /**
   * Nombre legible del autor — texto libre. En contexto autenticado se
   * rellena con el nombre del perfil; en share anónimo, con el nombre
   * que el usuario introduzca al hacer su primera anotación. Puede ser
   * undefined para anotaciones antiguas (compat).
   */
  autorNombre?: string
}

export interface PointAnnotation extends BaseAnnotation {
  type: 'point'
  /** Posición normalizada 0..1. */
  x: number
  y: number
  /** Texto del comentario. Puede ser vacío durante la edición inline. */
  text: string
}

export interface TextAnnotation extends BaseAnnotation {
  type: 'text'
  x: number
  y: number
  text: string
  /**
   * Tamaño de fuente en píxeles "lógicos" (sobre el viewport natural del PDF).
   * Si está ausente en datos antiguos, asumimos `DEFAULT_TEXT_SIZE` al renderizar.
   * Solo 3 valores válidos para mantener simplicidad — ver `TEXT_SIZES`.
   */
  fontSize?: number
}

export interface DrawAnnotation extends BaseAnnotation {
  type: 'draw'
  /** Puntos en orden de trazo, todos normalizados 0..1. */
  points: { x: number; y: number }[]
  /** Grosor del trazo en píxeles "lógicos" (a escala del canvas). */
  strokeWidth: number
}

/**
 * Línea recta entre dos puntos. Conservamos x1/y1 y x2/y2 (en vez de
 * normalizar a top-left + ancho/alto) para que más adelante podamos
 * añadir una variante con cabeza de flecha sin perder dirección.
 */
export interface LineAnnotation extends BaseAnnotation {
  type: 'line'
  x1: number
  y1: number
  x2: number
  y2: number
  strokeWidth: number
}

/**
 * Rectángulo con borde y relleno opcional. (x, y) es la esquina superior izquierda;
 * (w, h) son ancho y alto positivos. La capa que detecta el gesto se
 * encarga de normalizar el drag (cualquier dirección → top-left + size).
 */
export interface RectAnnotation extends BaseAnnotation {
  type: 'rect'
  x: number
  y: number
  w: number
  h: number
  strokeWidth: number
  /** Ausente en datos antiguos equivale a false para mantenerlos transparentes. */
  filled?: boolean
}

/**
 * Elipse con borde y relleno opcional. Misma caja delimitadora que RectAnnotation
 * para tratamiento uniforme — el render usa cx=x+w/2, cy=y+h/2, rx=w/2,
 * ry=h/2. Cuando w === h, visualmente es un círculo.
 */
export interface EllipseAnnotation extends BaseAnnotation {
  type: 'ellipse'
  x: number
  y: number
  w: number
  h: number
  strokeWidth: number
  /** Ausente en datos antiguos equivale a false para mantenerlos transparentes. */
  filled?: boolean
}

/**
 * Imagen que el usuario pega encima del plano: foto de una puerta, captura
 * de un detalle, marca de obra. El blob vive en R2; el cliente recibe una
 * `signedUrl` temporal (TTL 1h) embebida en el listado de anotaciones para
 * renderizar sin un round-trip extra.
 *
 *  - `objectKey`: path persistido en R2 (lo devuelve el endpoint de upload).
 *  - `mime`: png / jpeg / webp.
 *  - `signedUrl`: NO se persiste en BD ni se manda al backend al guardar —
 *    lo inyecta el backend en cada list response. En cliente lo tratamos
 *    como opcional porque mientras pdf-lib quema podría no estar fresco.
 */
export interface ImageAnnotation extends BaseAnnotation {
  type: 'image'
  /** Top-left normalizado 0..1. */
  x: number
  y: number
  /** Tamaño normalizado 0..1. */
  w: number
  h: number
  objectKey: string
  mime: 'image/png' | 'image/jpeg'
  /** Solo llega en el wire del backend; null mientras el cliente está
   *  creando la anotación. Se usa para renderizar el <image> en SVG. */
  signedUrl?: string
}

export type Annotation =
  | PointAnnotation
  | TextAnnotation
  | DrawAnnotation
  | LineAnnotation
  | RectAnnotation
  | EllipseAnnotation
  | ImageAnnotation

/**
 * Paleta cerrada de colores. Mantener corta para no abrumar y para que el
 * resultado visual siga siendo coherente entre usuarios. Rojo por defecto:
 * es el color tradicional de corrección en arquitectura/ingeniería.
 */
export const ANNOTATION_COLORS = [
  { id: 'red', hex: '#DC2626', label: 'Rojo' },
  { id: 'blue', hex: '#2563EB', label: 'Azul' },
  { id: 'green', hex: '#16A34A', label: 'Verde' },
  { id: 'amber', hex: '#D97706', label: 'Naranja' },
  { id: 'black', hex: '#18181B', label: 'Negro' },
] as const

/**
 * Opacidad del RELLENO de rect/ellipse. Translúcida por diseño y sin
 * interruptor (decisión 2026-09-04): sobre un plano, una forma rellena debe
 * marcar la zona sin ocultar lo que hay debajo — con relleno opaco, rellenar
 * era taparse a uno mismo el dibujo que se estaba anotando. El borde va
 * aparte y sigue opaco. La usan el visor (AnnotationLayer) y el export
 * quemado (pdfBurn): lo que se descarga es lo que se ve.
 */
export const RELLENO_OPACIDAD = 0.25

export type AnnotationColorId = (typeof ANNOTATION_COLORS)[number]['id']

export const DEFAULT_ANNOTATION_COLOR = ANNOTATION_COLORS[0].hex

/**
 * Grosores predefinidos del trazo del lápiz. Valores en píxeles "lógicos"
 * (sobre el viewport del PDF, no del viewport del usuario). El zoom CSS
 * los escala visualmente sin perder fidelidad de coordenadas.
 */
export const STROKE_WIDTHS = [
  { id: 'thin', value: 1.5, label: 'Fino' },
  { id: 'medium', value: 2.5, label: 'Medio' },
  { id: 'thick', value: 4, label: 'Grueso' },
] as const

export const DEFAULT_STROKE_WIDTH = STROKE_WIDTHS[1].value

/**
 * Tamaños de texto disponibles para TextAnnotation. Valores en px "lógicos"
 * sobre el viewport del PDF (el zoom CSS los escala visualmente sin pérdida).
 * Tres niveles a propósito — más opciones añaden ruido sin ganancia real.
 */
export const TEXT_SIZES = [
  { id: 'sm', value: 11, label: 'Pequeño' },
  { id: 'md', value: 14, label: 'Medio' },
  { id: 'lg', value: 20, label: 'Grande' },
] as const

export const DEFAULT_TEXT_SIZE = TEXT_SIZES[1].value
